import 'braid-design-system/reset';
import React from 'react';
import { VocabProvider } from '@vocab/react';
import loadable from 'sku/@loadable/component';
import { Route, Switch } from 'react-router-dom';
import { observer, Provider } from 'mobx-react';
import { QueryParamProvider } from 'use-query-params';
import { StoreProvider } from '../utils/hooks/useStores';
import './App.css';
import { configure } from 'mobx';
import { updateClientStore } from '../graphql/client';
import { ApolloProvider } from '@apollo/client';
import client from '../graphql/hirer/client';
import { AppTitle } from 'src/components/app/AppTitle';
import { BraidProvider } from 'braid-design-system';
import apacTheme from 'braid-design-system/themes/apac';
import type { EmployerSiteNames, Environment } from 'src/siteConfig';
import { AppConfigProvider } from 'src/constants/AppConfig/AppConfigProvider';
import type { Locale } from '@seek/melways-sites';
import urls from 'src/constants/urls';
import getEnabledFeatures from 'src/utils/features';
import Store from 'src/stores/Store';

configure({ useProxies: 'never' });

const CandidateManagement = loadable(() => import('./CandidateManagementPage'));
const ActionsPage = loadable(() => import('./ActionsPage'));
const YourCandidatesPage = loadable(() => import('./YourCandidatesPage'));

export interface AppConfig {
  site: EmployerSiteNames;
  environment: Environment;
  locale: Locale;
}
interface Props {
  config: AppConfig;
}

const App: React.FC<Props> = ({ config }) => {
  const [store] = React.useState(() =>
    Store.create({
      site: config.site,
      locale: config.locale,
      featureStore: {
        featuresEnabled: getEnabledFeatures(config.environment),
      },
    }),
  );

  const renderCandidateManagement = () => {
    updateClientStore(store);
    return <CandidateManagement />;
  };

  const renderYourCandidatesPage = () => (
    <YourCandidatesPage
      environment={config.environment}
      site={config.site}
      locale={config.locale}
    />
  );

  return (
    <ApolloProvider client={client}>
      <VocabProvider language={config.locale}>
        <QueryParamProvider ReactRouterRoute={Route}>
          <AppConfigProvider
            locale={config.locale}
            site={config.site}
            environment={config.environment}
          >
            <StoreProvider initialStore={store}>
              <AppTitle site={config.site} />
              <Provider store={store}>
                <BraidProvider theme={apacTheme}>
                  <Switch>
                    <Route
                      exact
                      path={`${urls.OPTIONAL_LANGUAGE_SEGMENT}${urls.CANDIDATE_MANAGEMENT}`}
                      render={renderCandidateManagement}
                    />
                    <Route
                      path={`${urls.OPTIONAL_LANGUAGE_SEGMENT}${urls.ACTIONS}`}
                      render={() => <ActionsPage />}
                    />
                    <Route
                      path={`${urls.OPTIONAL_LANGUAGE_SEGMENT}${urls.YOUR_CANDIDATES}`}
                      render={renderYourCandidatesPage}
                    />
                  </Switch>
                </BraidProvider>
              </Provider>
            </StoreProvider>
          </AppConfigProvider>
        </QueryParamProvider>
      </VocabProvider>
    </ApolloProvider>
  );
};

export default observer(App);
